/**
 * executionPhaseDetails - Regra pura (BR-WF-EXEC-002): liga cada passo da
 * página Execuções ao detalhe da fase devolvido pela linha do tempo.
 *
 * Os passos usam o bloco da VERSÃO em que o processo rodou; a linha do tempo
 * devolve o bloco traduzido para a versão VIGENTE. Por isso a ligação tenta,
 * em ordem: mesmo bloco → mesmo tipo + nome → mesma posição entre os do tipo.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-17 v1.0.0 - Criação (corrige detalhes vazios em execuções antigas).
 */

export interface PassoExecucao {
  node_id?: string | null;
  node_type?: string | null;
  node_label?: string | null;
}

export interface EtapaLinhaDoTempo<D> {
  node_id: string;
  node_type?: string | null;
  label?: string | null;
  detalhes?: D | null;
}

const normalizar = (v?: string | null) => (v ?? '').trim().toLowerCase();

/** Devolve, para cada passo (mesma ordem), o detalhe correspondente ou null. */
export function ligarDetalhesAosPassos<D>(
  passos: PassoExecucao[],
  etapas: EtapaLinhaDoTempo<D>[],
): (D | null)[] {
  const usadas = new Set<number>();
  const contagemTipo = new Map<string, number>();
  return passos.map((p) => {
    const tipo = normalizar(p.node_type);
    const posTipo = contagemTipo.get(tipo) ?? 0;
    contagemTipo.set(tipo, posTipo + 1);

    // 1) mesmo bloco (execução na versão vigente)
    let idx = etapas.findIndex((e, i) => !usadas.has(i) && !!p.node_id && e.node_id === p.node_id);
    // 2) mesmo tipo + mesmo nome
    if (idx < 0) {
      idx = etapas.findIndex(
        (e, i) =>
          !usadas.has(i) && normalizar(e.node_type) === tipo && normalizar(e.label) === normalizar(p.node_label),
      );
    }
    // 3) mesma posição entre as etapas do mesmo tipo
    if (idx < 0) {
      const doTipo = etapas.map((e, i) => ({ e, i })).filter(({ e }) => normalizar(e.node_type) === tipo);
      const alvo = doTipo[posTipo];
      if (alvo && !usadas.has(alvo.i)) idx = alvo.i;
    }
    if (idx < 0) return null;
    usadas.add(idx);
    return etapas[idx].detalhes ?? null;
  });
}
